import React, { useState, useEffect } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AxiosUrl from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { EditorState, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import { stateToHTML } from 'draft-js-export-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import styles from './EditBrandModal.module.css';

function sanitizeSlug(value) {
    return value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

function EditBrandModal({ setOpenModal, data, setRefresh }) {
    const uid = localStorage.getItem('id');
    const [brandName, setBrandName] = useState(data.name || '');
    const [message, setMessage] = useState(data.message || '');
    const [website, setWebsite] = useState(data.website || '');
    const [loading, setLoading] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [imageUrl, setImageUrl] = useState(data.imgUrl || '');
    const [status, setStatus] = useState(data.status || 0);
    const [activeTab, setActiveTab] = useState('basic');

    // SEO Fields
    const [metaTitle, setMetaTitle] = useState(data.metaTitle || '');
    const [metaDescription, setMetaDescription] = useState(data.metaDescription || '');
    const [slug, setSlug] = useState(data.slug ? sanitizeSlug(data.slug) : '');
    const [slugTouched, setSlugTouched] = useState(!!data.slug);
    const [focusKeywords, setFocusKeywords] = useState(
        Array.isArray(data.focusKeywords)
            ? data.focusKeywords
            : (typeof data.focusKeywords === 'string' && data.focusKeywords.trim()
                ? data.focusKeywords.split(',').map(s => s.trim()).filter(Boolean)
                : [])
    );
    const [keywordInput, setKeywordInput] = useState('');
    const [brandDescription, setBrandDescription] = useState(data.brandDescription || '');
    const [brandTagline, setBrandTagline] = useState(data.brandTagline || '');

    // Article Fields
    const [articleEnabled, setArticleEnabled] = useState(data.brandArticle?.enabled || false);
    const [articleTitle, setArticleTitle] = useState(data.brandArticle?.title || '');
    const [articleContent, setArticleContent] = useState(data.brandArticle?.content || '');
    const [editorState, setEditorState] = useState(() => {
        const existing = data.brandArticle?.content || '';
        if (existing) {
            const block = htmlToDraft(existing);
            if (block) {
                const cs = ContentState.createFromBlockArray(block.contentBlocks, block.entityMap);
                return EditorState.createWithContent(cs);
            }
        }
        return EditorState.createEmpty();
    });
    const [articlePosition, setArticlePosition] = useState(data.brandArticle?.position || 'below_products');

    // FAQ Fields
    const [faqs, setFaqs] = useState(
        Array.isArray(data.faqs) && data.faqs.length > 0
            ? data.faqs.map(f => ({ question: f.question || '', answer: f.answer || '', gid: f.gid || Date.now().toString() }))
            : []
    );

    const [aiLoading, setAiLoading] = useState(false);

    const handleClose = () => setOpenModal(false);

    const [showAiPrompt, setShowAiPrompt] = useState(false);
    const [aiAbout, setAiAbout] = useState(data.brandDescription || data.message || '');
    const [aiProducts, setAiProducts] = useState(
        (Array.isArray(data.focusKeywords) ? data.focusKeywords.join(', ') : (data.focusKeywords || ''))
        || data.brandTagline || ''
    );
    const [aiSection, setAiSection] = useState('all');

    const startAIGenerate = () => {
        if (!brandName.trim()) { toast.error('Enter brand name first'); return; }
        setShowAiPrompt(true);
    };

    const handleAIGenerate = async () => {
        setShowAiPrompt(false);
        setAiLoading(true);
        const token = localStorage.getItem('token');
        try {
            const res = await fetch('https://api.ecomtechbd.com/brand/ai-generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', token, uid },
                body: JSON.stringify({ brandName: brandName.trim(), website, message, aboutBrand: aiAbout.trim(), productTypes: aiProducts.trim(), section: aiSection }),
            });
            if (!res.ok) throw new Error('Server error');
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const d = JSON.parse(line.slice(6));
                        if (d.type === 'complete' && d.success) {
                            const r = d.data;
                            const sec = aiSection;
                            if ((sec === 'all' || sec === 'seo') && r.metaTitle) setMetaTitle(r.metaTitle);
                            if ((sec === 'all' || sec === 'seo') && r.metaDescription) setMetaDescription(r.metaDescription);
                            if ((sec === 'all' || sec === 'seo') && r.brandTagline) setBrandTagline(r.brandTagline);
                            if ((sec === 'all' || sec === 'seo') && r.brandDescription) setBrandDescription(r.brandDescription);
                            if ((sec === 'all' || sec === 'seo') && r.focusKeywords?.length) setFocusKeywords(r.focusKeywords);
                            if ((sec === 'all' || sec === 'faqs') && r.faqs?.length) setFaqs(r.faqs.map((f, i) => ({ ...f, gid: String(Date.now() + i) })));
                            if ((sec === 'all' || sec === 'article') && r.articleTitle) { setArticleTitle(r.articleTitle); setArticleEnabled(true); }
                            if ((sec === 'all' || sec === 'article') && r.articleContent) {
                                setArticleContent(r.articleContent);
                                setArticleEnabled(true);
                                const blocks = htmlToDraft(r.articleContent);
                                if (blocks.contentBlocks) {
                                    const cs = ContentState.createFromBlockArray(blocks.contentBlocks, blocks.entityMap);
                                    setEditorState(EditorState.createWithContent(cs));
                                }
                            }
                            toast.success(`AI content generated! (${d.stats?.totalDuration || ''}s)`);
                        } else if (d.type === 'complete' && !d.success) {
                            toast.error(d.message || 'Generation failed');
                        }
                    } catch {}
                }
            }
        } catch (err) {
            toast.error('Failed: ' + err.message);
        } finally {
            setAiLoading(false);
        }
    };

    useEffect(() => {
        if (brandName && !slugTouched) setSlug(sanitizeSlug(brandName));
    }, [brandName, slugTouched]);

    const handleEditorChange = (newState) => {
        setEditorState(newState);
        const html = stateToHTML(newState.getCurrentContent());
        setArticleContent(html === '<p><br></p>' ? '' : html);
    };

    const handleAddKeyword = (e) => {
        if (e.key === 'Enter' && keywordInput.trim()) {
            e.preventDefault();
            if (!focusKeywords.includes(keywordInput.trim())) setFocusKeywords([...focusKeywords, keywordInput.trim()]);
            setKeywordInput('');
        }
    };

    const handleDeleteKeyword = (kw) => setFocusKeywords(focusKeywords.filter(k => k !== kw));
    const addFaq = () => setFaqs([...faqs, { question: '', answer: '', gid: Date.now().toString() }]);
    const removeFaq = (i) => setFaqs(faqs.filter((_, idx) => idx !== i));
    const updateFaq = (i, field, value) => {
        const updated = [...faqs];
        updated[i] = { ...updated[i], [field]: value };
        setFaqs(updated);
    };

    const handleSave = () => {
        setLoading(true);
        if (!brandName) { toast.error('Brand name is required'); setLoading(false); return; }

        const formData = {
            uid,
            brandName,
            imgUrl: imageUrl,
            oldBrand: data.name,
            message,
            website,
            status,
            metaTitle: metaTitle || brandName,
            metaDescription,
            slug: slug || sanitizeSlug(brandName),
            focusKeywords,
            brandDescription,
            brandTagline,
            brandArticle: { enabled: articleEnabled, title: articleTitle, content: articleContent, position: articlePosition },
            faqs: faqs.filter(f => f.question.trim() && f.answer.trim()),
        };

        AxiosUrl.post(`/edit-brand/${uid}`, formData)
            .then(() => {
                toast.success('Brand updated successfully');
                setRefresh((prev) => prev + 1);
                handleClose();
                setLoading(false);
            })
            .catch(() => { toast.error('Failed to update brand'); setLoading(false); });
    };

    const cdnImage = () => {
        const image = new FormData();
        image.append('photo', photo);
        axios.post('https://cdn.ecomtechbd.com/upload/category', image, {
            headers: { 'Content-Type': 'multipart/form-data', token: 'zesrxdyctuutydrszrestdrxy' },
        }).then((res) => { toast.success('Image uploaded'); setImageUrl(res.data); })
          .catch(() => toast.error('Failed to upload image'));
    };

    useEffect(() => { if (photo) cdnImage(); }, [photo]);

    const handleFileChange = (e) => setPhoto(e.target.files[0]);

    const renderBasicTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.imageUploadSection}>
                {imageUrl ? (
                    <div className={styles.imagePreviewContainer}>
                        <img src={imageUrl} alt="Brand" className={styles.imagePreview} />
                        <label className={styles.reuploadOverlay}>
                            <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                            <CloudUploadIcon />
                            <span>Re-upload</span>
                        </label>
                    </div>
                ) : (
                    <label className={styles.uploadButton}>
                        <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                        <CloudUploadIcon />
                        <span>Upload Image</span>
                    </label>
                )}
            </div>
            <div className={styles.formGroup}>
                <label>Brand Name *</label>
                <input type="text" value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Enter brand name" />
            </div>
            <div className={styles.formGroup}>
                <label>Brand Website (Optional)</label>
                <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://brand-website.com" />
            </div>
            <div className={styles.formGroup}>
                <label>Brand Message (Optional)</label>
                <input type="text" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Optional promotional message" />
            </div>
            <div className={styles.formGroup}>
                <label>Status</label>
                <select value={status} onChange={(e) => setStatus(parseInt(e.target.value))}>
                    <option value={0}>Active</option>
                    <option value={1}>Inactive</option>
                </select>
            </div>
        </div>
    );

    const renderSEOTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>SEO Title</label>
                <input type="text" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} placeholder={brandName || 'Auto-generated from brand name'} />
                <small className={`${styles.characterCounter} ${metaTitle.length > 60 ? styles.warning : ''}`}>{metaTitle.length}/60 characters</small>
            </div>
            <div className={styles.formGroup}>
                <label>Meta Description</label>
                <textarea value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} placeholder="Brief description for search engines" rows={3} />
                <small className={`${styles.characterCounter} ${metaDescription.length > 160 ? styles.warning : ''}`}>{metaDescription.length}/160 characters</small>
            </div>
            <div className={styles.formGroup}>
                <label>URL Slug</label>
                <input type="text" value={slug} onChange={(e) => { setSlugTouched(true); setSlug(sanitizeSlug(e.target.value)); }} placeholder="auto-generated-slug" />
                <small>URL-friendly slug for this brand</small>
            </div>
            <div className={styles.formGroup}>
                <label>Focus Keywords</label>
                <input type="text" value={keywordInput} onChange={(e) => setKeywordInput(e.target.value)} onKeyDown={handleAddKeyword} placeholder="Type a keyword and press Enter" />
                <small>Press Enter to add each keyword</small>
            </div>
            {focusKeywords.length > 0 && (
                <div className={styles.keywordChipsContainer}>
                    {focusKeywords.map((kw) => (
                        <div key={kw} className={styles.keywordChip}>
                            <span>{kw}</span>
                            <button onClick={() => handleDeleteKeyword(kw)}>×</button>
                        </div>
                    ))}
                </div>
            )}
            <div className={styles.formGroup}>
                <label>Brand Tagline</label>
                <input type="text" value={brandTagline} onChange={(e) => setBrandTagline(e.target.value)} placeholder="Short promotional tagline" />
            </div>
            <div className={styles.formGroup}>
                <label>Brand Description</label>
                <textarea value={brandDescription} onChange={(e) => setBrandDescription(e.target.value)} placeholder="Long-form description for brand page (300-500 words recommended)" rows={6} />
                <small>For SEO and content purposes</small>
            </div>
        </div>
    );

    const renderArticleTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionInfo}>
                Add an SEO article to the brand page.
            </div>
            <div className={styles.toggleRow}>
                <label>Enable Article</label>
                <button type="button" className={`${styles.toggleSwitch} ${articleEnabled ? styles.active : ''}`} onClick={() => setArticleEnabled(!articleEnabled)} />
            </div>
            {articleEnabled && (
                <>
                    <div className={styles.formGroup}>
                        <label>Article Title</label>
                        <input type="text" value={articleTitle} onChange={(e) => setArticleTitle(e.target.value)} placeholder="e.g., About This Brand" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Article Content</label>
                        <div className={styles.editorWrapper}>
                            <Editor
                                editorState={editorState}
                                onEditorStateChange={handleEditorChange}
                                wrapperClassName={styles.editorWrapperInner}
                                editorClassName={styles.editorContent}
                                toolbarClassName={styles.editorToolbar}
                                placeholder="Write your article content here..."
                                toolbar={{
                                    options: ['inline', 'blockType', 'list', 'textAlign', 'link', 'history'],
                                    inline: { inDropdown: false, options: ['bold', 'italic', 'underline', 'strikethrough'] },
                                    blockType: { inDropdown: true, options: ['Normal', 'H2', 'H3', 'H4', 'Blockquote'] },
                                    list: { inDropdown: false, options: ['unordered', 'ordered'] },
                                    textAlign: { inDropdown: true },
                                    link: { inDropdown: false, showOpenOptionOnHover: true, defaultTargetOption: '_blank' },
                                    history: { inDropdown: false, options: ['undo', 'redo'] },
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Article Position</label>
                        <select value={articlePosition} onChange={(e) => setArticlePosition(e.target.value)}>
                            <option value="below_products">Below Products (Default)</option>
                            <option value="above_products">Above Products</option>
                        </select>
                    </div>
                </>
            )}
        </div>
    );

    const renderFAQsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionInfo}>Add FAQs to generate FAQ Schema markup for better SEO.</div>
            {faqs.length === 0 && <div className={styles.emptyFaqMessage}>No FAQs added yet.</div>}
            {faqs.map((faq, i) => (
                <div key={faq.gid} className={styles.faqItem}>
                    <div className={styles.faqItemHeader}>
                        <span>FAQ #{i + 1}</span>
                        <button type="button" className={styles.removeFaqBtn} onClick={() => removeFaq(i)}>×</button>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Question</label>
                        <input type="text" value={faq.question} onChange={(e) => updateFaq(i, 'question', e.target.value)} placeholder="Enter the FAQ question" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Answer</label>
                        <textarea value={faq.answer} onChange={(e) => updateFaq(i, 'answer', e.target.value)} placeholder="Enter the answer" rows={3} />
                    </div>
                </div>
            ))}
            <button type="button" className={styles.addFaqBtn} onClick={addFaq}>+ Add FAQ</button>
        </div>
    );

    return (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <ToastContainer />
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Edit Brand</h2>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                            type="button"
                            onClick={startAIGenerate}
                            disabled={aiLoading}
                            style={{ padding: '6px 14px', background: aiLoading ? '#e8e8ee' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: aiLoading ? '#6d7175' : '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: aiLoading ? 'not-allowed' : 'pointer' }}
                        >
                            {aiLoading ? 'Generating...' : 'AI Generate'}
                        </button>
                        <button className={styles.closeButton} onClick={handleClose}>×</button>
                    </div>
                </div>
                {showAiPrompt && (
                    <div style={{ padding: '16px 24px', background: '#f0f0ff', borderBottom: '1px solid #e1e3e5' }}>
                        <p style={{ fontSize: '13px', fontWeight: 600, color: '#202223', marginBottom: '10px' }}>Tell AI about this brand:</p>
                        <input
                            type="text"
                            placeholder="What does this brand do? e.g. Premium women's bags and accessories"
                            value={aiAbout}
                            onChange={e => setAiAbout(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }}
                        />
                        <input
                            type="text"
                            placeholder="Products they sell? e.g. Handbags, wallets, clutches, cosmetics"
                            value={aiProducts}
                            onChange={e => setAiProducts(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
                            <select
                                value={aiSection}
                                onChange={e => setAiSection(e.target.value)}
                                style={{ padding: '6px 10px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '12px', background: '#fff' }}
                            >
                                <option value="all">Regenerate All</option>
                                <option value="seo">SEO Only</option>
                                <option value="article">Article Only</option>
                                <option value="faqs">FAQs Only</option>
                            </select>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => setShowAiPrompt(false)} style={{ padding: '6px 14px', background: '#fff', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={handleAIGenerate} style={{ padding: '6px 14px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Generate</button>
                            </div>
                        </div>
                    </div>
                )}
                <div className={styles.tabs}>
                    <button className={activeTab === 'basic' ? styles.activeTab : ''} onClick={() => setActiveTab('basic')}>Basic Info</button>
                    <button className={activeTab === 'seo' ? styles.activeTab : ''} onClick={() => setActiveTab('seo')}>SEO Settings</button>
                    <button className={activeTab === 'article' ? styles.activeTab : ''} onClick={() => setActiveTab('article')}>Article</button>
                    <button className={activeTab === 'faqs' ? styles.activeTab : ''} onClick={() => setActiveTab('faqs')}>FAQs</button>
                </div>
                {activeTab === 'basic' && renderBasicTab()}
                {activeTab === 'seo' && renderSEOTab()}
                {activeTab === 'article' && renderArticleTab()}
                {activeTab === 'faqs' && renderFAQsTab()}
                <div className={styles.modalFooter}>
                    <button className={styles.cancelButton} onClick={handleClose}>Cancel</button>
                    <button className={styles.submitButton} onClick={handleSave} disabled={loading}>
                        {loading ? 'Updating...' : 'Update Brand'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default EditBrandModal;
